import { Outlet } from "react-router";
import { Toaster } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";
import {
    HomeIcon,
    ReceiptIcon,
    TagIcon,
    FileSpreadsheetIcon,
    //PieChartIcon,
} from "lucide-react";
import { type LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { fetchAuthSession } from "@aws-amplify/auth";
import { NavigationRail } from "./navigation/rail";
import {
    NavigationRailProvider,
    NavigationRailContent,
} from "@/components/navigation/rail-context";
import { AppBarProvider } from "./navigation/appbar-context";
import { AppBar } from "./navigation/appbar";
import { useIsMobile } from "@/hooks/use-mobile";

interface LayoutProps {
    signOut?: () => void;
}

interface NavGroup {
    title: string;
    items: NavItem[];
}

interface NavItem {
    title: string;
    url: string;
    icon: LucideIcon;
    requiresAdmin?: boolean;
}

const navGroups: NavGroup[] = [
    {
        title: "Application",
        items: [
            {
                title: "Home",
                url: "/",
                icon: HomeIcon,
            },
            {
                title: "Receipts",
                url: "/receipts",
                icon: ReceiptIcon,
            },
            // {
            //     title: "Controlling",
            //     url: "/controlling",
            //     icon: PieChartIcon,
            // },
            {
                title: "Reports",
                url: "/reports",
                icon: FileSpreadsheetIcon,
            },
            {
                title: "Tags",
                url: "/tags",
                icon: TagIcon,
            },
        ],
    },
    /* Moved to user menu */
    // {
    //     title: "Administration",
    //     items: [
    //         {
    //             title: "Users",
    //             url: "/users",
    //             icon: UsersIcon,
    //             requiresAdmin: true,
    //         },
    //         {
    //             title: "Usage",
    //             url: "/usage",
    //             icon: ChartColumnIncreasingIcon,
    //             requiresAdmin: true,
    //         },
    //     ],
    // },
];

export function Layout({ signOut }: LayoutProps) {
    const [isAdmin, setIsAdmin] = useState(false);
    const [userName, setUserName] = useState("User");
    const isMobile = useIsMobile();

    useEffect(() => {
        checkUserGroups();
        fetchUserInfo();
    }, []);

    const checkUserGroups = async () => {
        try {
            const { tokens } = await fetchAuthSession();
            const payload = tokens?.idToken?.payload;
            const groups = payload?.["cognito:groups"] || [];
            setIsAdmin(
                Array.isArray(groups) ? groups.includes("wheel") : false,
            );
        } catch (error) {
            console.error("Error checking user groups:", error);
        }
    };

    const fetchUserInfo = async () => {
        try {
            const { tokens } = await fetchAuthSession();
            const payload = tokens?.idToken?.payload;

            if (payload) {
                const givenName = payload.given_name as string | undefined;
                const familyName = payload.family_name as string | undefined;

                if (givenName && familyName) {
                    setUserName(`${givenName} ${familyName}`);
                } else if (givenName) {
                    setUserName(givenName);
                } else if (payload.email) {
                    setUserName(payload.email as string);
                }
            }
        } catch (error) {
            console.error("Error fetching user info:", error);
        }
    };

    return (
        <AppBarProvider>
            <NavigationRailProvider defaultExpanded={false}>
                <NavigationRail
                    groups={navGroups}
                    isAdmin={isAdmin}
                    userName={userName}
                    signOut={signOut}
                />

                <NavigationRailContent className="relative">
                    {/* top padding defined in app bar */}
                    <main
                        className={cn(
                            "h-screen overflow-y-auto",
                            "px-4 -sm:px-12 -lg:px-8 pb-8",
                            !isMobile && "pt-4",
                        )}
                    >
                        <AppBar title={"Receiptrex"} showOnlyOnMobile={true} />
                        <Outlet />
                        <Toaster position="bottom-left" />
                    </main>
                </NavigationRailContent>
            </NavigationRailProvider>
        </AppBarProvider>
    );
}
